import React, { useMemo, useRef, useState } from 'react';
import { ProductColor, ProductBuyboxData, VariantDetails, VariantsByKey } from '../../integrations/relewise/types/mapped';
import { ImageWithLoading } from '../../shared/components/image-with-loading';
import { useImageSettings } from '../../shared/hooks/useImageSettings';
import { ICusNewBuyboxData } from './cus-new-buybox.data';
import { ICusNewBuyboxProps } from './cus-new-buybox.props.autogenerated';
import classnames from 'classnames';
import { IDictionary } from '@msdyn365-commerce/retail-proxy';
import { useMappingService } from '../../integrations/relewise/hooks/serviceHooks';
import { RelewiseMappingService } from '../../integrations/relewise/services/MappingService';
import { Button } from '../../components';
import { CollapsibleGroup } from '../../shared/components/collapsible-group';
import { useScrollLock } from '../../shared/hooks/useScrollLock';
import { useWishlist } from '../../shared/hooks/useWishlist';
import { useBuyboxAvailability } from './hooks/useBuyboxAvailability';
import { useBuyboxAddToCart } from './hooks/useBuyboxAddToCart';
import { useProductViewTracker } from '../../integrations/relewise/hooks/useProductViewTracker';
import { useProductMetaInfoData } from './hooks/useProductMetaInfoData';
import { useBuyboxQuerySetter } from './hooks/useBuyboxQuerySetter';
import { useRedirectBuyboxToErrorPage } from './hooks/useRedirectBuyboxToErrorPage';
import { OverflowTooltip } from '../../shared/components/overflow-tooltip';
import { InfoTooltip } from '../../shared/components/info-tooltip/info-tooltip';
import { HtmlHeadInclude, ImageFetchPriority } from '@msdyn365-commerce/core';
import { useIsMobile } from '../../shared/hooks/useIsMobile';
import { BuyboxHeadMetadata } from './components/buybox-head-metadata';
import { usePdpDatalayer } from '../../integrations/datalayer/hooks/usePdpDatalayer';
import { sanitizeHtmlId } from '../../shared/functions/string-transformers';
import { NEW_BUYBOX_MODULE_NAME } from './cus-new-buybox.const';
import { useLogBuyboxMissingFields } from './hooks/useLogBuyboxMissingFields';

const IMAGE_VIEWPORT_SETTINGS = {
    xs: { w: 840, h: 1120 },
    sm: { w: 960, h: 1280 },
    md: { w: 1125, h: 1500 },
    lg: { w: 1125, h: 1500 },
    xl: { w: 1125, h: 1500 }
};

const NO_SCROLL_INDEX = -1;

const getDefaultVariant = (
    variantsByKey: VariantsByKey | undefined,
    requestedVariantKey: string | undefined
): VariantDetails | undefined => {
    if (!variantsByKey) {
        return undefined;
    }
    if (requestedVariantKey) {
        return variantsByKey[requestedVariantKey] ?? Object.values(variantsByKey)[0];
    }
    return Object.values(variantsByKey)[0];
};

interface GetInitialSelectionArgs {
    productBuyboxData: ProductBuyboxData | undefined;
    defaultVariant: VariantDetails | undefined;
    defaultColor: ProductColor | undefined;
    query: IDictionary<string> | undefined;
    relewiseMappingService: RelewiseMappingService;
}
const getInitialSelection = ({
    productBuyboxData,
    defaultVariant,
    defaultColor,
    query,
    relewiseMappingService
}: GetInitialSelectionArgs) => {
    if (!productBuyboxData || !productBuyboxData.productItemId) {
        return {
            color: defaultColor,
            size: undefined,
            variant: defaultVariant
        };
    }

    // When url has variant recordId and no queryparams, select that variant
    if (productBuyboxData.requestedVariantKey && !query?.color && !query?.size) {
        return {
            color: defaultColor,
            size: defaultVariant?.size,
            variant: defaultVariant
        };
    }

    const requestedColor = query?.color ? productBuyboxData.allColors?.find(c => c.name === query.color) : undefined;
    const color = requestedColor ?? defaultColor;
    if (!color?.name) {
        return {
            color,
            size: undefined,
            variant: defaultVariant
        };
    }

    const availableSizesForColor = productBuyboxData.compatibleSizesByColor[color.name] ?? [];
    const firstAvailableSize = availableSizesForColor[0];
    let size: string | undefined;
    if (query?.size && availableSizesForColor.includes(query.size)) {
        size = query.size;
    } else if (availableSizesForColor.length === 1) {
        // If there is only one available size, select it
        size = firstAvailableSize;
    }

    const variantKey = relewiseMappingService.mapProductDimensionsToRelewiseVariantId(
        productBuyboxData.productItemId,
        color.name,
        size ?? firstAvailableSize
    );

    return {
        color,
        size,
        variant: productBuyboxData.variantsByKey?.[variantKey] ?? defaultVariant
    };
};

const NewBuybox = (props: ICusNewBuyboxProps<ICusNewBuyboxData>) => {
    const { data, resources, context } = props;
    const gridSettings = context.request.gridSettings;
    const query = context.request.query;
    const productBuyboxData = data.productBuyboxData.result;
    const {
        allColors,
        variantsByKey,
        compatibleSizesByColor,
        productMetaInfo,
        productItemId,
        productRecordId,
        relewiseProductId,
        allSizes,
        brand,
        displayName,
        masterProductImages
    } = productBuyboxData ?? {};

    const imageSettings = useImageSettings(IMAGE_VIEWPORT_SETTINGS, true);
    const relewiseMappingService = useMappingService(context.request, context.telemetry);
    const isMobile = useIsMobile(context);

    const defaultVariant = getDefaultVariant(variantsByKey, productBuyboxData?.requestedVariantKey);
    const defaultColor: ProductColor | undefined = defaultVariant?.color;
    const initialSelection = useMemo(
        () => getInitialSelection({ productBuyboxData, defaultVariant, defaultColor, query, relewiseMappingService }),
        [productBuyboxData, defaultVariant, defaultColor, query, relewiseMappingService]
    );

    const [selectedVariant, setSelectedVariant] = useState(initialSelection.variant);
    const [selectedColor, setSelectedColor] = useState<ProductColor | undefined>(initialSelection.color);
    const [selectedSize, setSelectedSize] = useState<string | undefined>(initialSelection.size);
    const [selectedImage, setSelectedImage] = useState<string | undefined>(initialSelection.variant?.imgUrl);
    const [isSizeModalOpen, setIsSizeModalOpen] = useState(false);

    useRedirectBuyboxToErrorPage(productBuyboxData, context.request.sitePath, context.telemetry);
    useScrollLock(isSizeModalOpen);
    useBuyboxQuerySetter(selectedColor, selectedSize);
    useProductViewTracker(relewiseProductId, selectedVariant?.primarySizeVariant?.relewiseVariantId, context.request, context.telemetry);

    useLogBuyboxMissingFields({
        hasLoadedProduct: !!productBuyboxData,
        variantsByKey,
        allColors,
        allSizes,
        displayName,
        selectedVariant,
        telemetry: context.telemetry
    });

    const {
        availabilityByColor,
        currentSelectionOutOfStock,
        availabilityForSelectedColorBySize,
        isLoading: isAvailabilityLoading
    } = useBuyboxAvailability({
        context,
        variantsByKey,
        allColors,
        selectedVariant,
        selectedColor,
        selectedSize,
        compatibleSizesByColor,
        productItemId
    });

    const {
        handleAddToCartClick,
        addToCartLoading,
        hasError: hasAddToCartError
    } = useBuyboxAddToCart({
        context,
        selectedVariant,
        productItemId,
        productRecordId,
        unitOfMeasure: productBuyboxData?.unitOfMeasure,
        telemetry: context.telemetry
    });

    const { trackAddToCart } = usePdpDatalayer({
        request: context.request,
        telemetry: context.telemetry,
        productItemId: productItemId,
        selectedVariant,
        selectedColor,
        selectedSize,
        compatibleSizesByColor,
        brand: productBuyboxData?.brand
    });

    const {
        isProductInWishlist,
        toggleWishlist,
        isLoading: isWishlistLoading,
        addToWishlistError,
        isWishlistReady
    } = useWishlist(context, NEW_BUYBOX_MODULE_NAME);
    const isVariantInWishlist = useMemo(
        () => isProductInWishlist(selectedVariant?.primarySizeVariant?.variantRecordId),
        [selectedVariant, isProductInWishlist]
    );

    const metaInfoData = useProductMetaInfoData({ productMetaInfo, resources, productItemId: productItemId });

    const availableSizes = compatibleSizesByColor && selectedColor ? compatibleSizesByColor[selectedColor.name] : [];
    const previewImages = useMemo(() => {
        if (!selectedVariant) {
            return;
        }
        return [...new Set([selectedVariant.imgUrl, ...(selectedVariant.previewImages ?? []), ...(masterProductImages ?? [])])];
    }, [selectedVariant, masterProductImages]);

    const isSsr = typeof window === 'undefined';
    const activeImage = selectedImage ?? previewImages?.[0];
    const preloadImageUrl = isSsr ? `${activeImage}?${isMobile ? imageSettings.xs?.q : imageSettings.lg?.q}` : activeImage;

    const carouselRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const lastScrollIndex = useRef(NO_SCROLL_INDEX);

    const handleCarouselScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const carousel = e.currentTarget;
        const index = Math.round(carousel.scrollLeft / carousel.clientWidth);
        if (index === lastScrollIndex.current || !previewImages) {
            return;
        }
        lastScrollIndex.current = index;

        const imageUrl = previewImages[index];
        setSelectedImage(imageUrl);
        scrollSliderToImage(imageUrl);
    };

    const scrollCarouselToIndex = (index: number) => {
        const carousel = carouselRef.current;
        if (!carousel) {
            return;
        }

        carousel.style.scrollBehavior = 'auto';
        carousel.scrollTo({ left: carousel.clientWidth * index });
        carousel.style.scrollBehavior = '';
    };

    const scrollSliderToImage = (imageUrl: string) => {
        const slider = sliderRef.current;
        if (!slider || !previewImages) {
            return;
        }

        const index = previewImages.indexOf(imageUrl);
        const thumb = slider.children[index >= 0 ? index : 0] as HTMLElement | undefined;

        if (!thumb) {
            return;
        }

        const isHorizontal = window.matchMedia('(max-width: 920px)').matches;

        if (isHorizontal) {
            slider.scrollTo({
                left: thumb.offsetLeft - slider.clientWidth / 2 + thumb.clientWidth / 2,
                behavior: 'smooth'
            });
        } else {
            slider.scrollTo({
                top: thumb.offsetTop - slider.clientHeight / 2 + thumb.clientHeight / 2,
                behavior: 'smooth'
            });
        }
    };

    const setSelectedVariantFromDimensions = (color: string, size: string) => {
        if (!productItemId || !variantsByKey) {
            return;
        }
        const variantKey = relewiseMappingService.mapProductDimensionsToRelewiseVariantId(productItemId, color, size);
        const variant = variantsByKey[variantKey];

        if (variant) {
            setSelectedVariant(variant);
        }
    };

    const handlePreviewImageClick = (imageUrl: string, index: number) => {
        setSelectedImage(imageUrl);
        scrollCarouselToIndex(index);
        scrollSliderToImage(imageUrl);
    };

    const handleSizeSelect = (size: string) => {
        setSelectedSize(size);
        setIsSizeModalOpen(false);
        if (selectedColor && size) {
            setSelectedVariantFromDimensions(selectedColor.name, size);
        }
    };

    const handleSizeModalKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            setIsSizeModalOpen(false);
        }
    };

    const handleColorSelect = (color: ProductColor) => {
        if (color.name === selectedColor?.name) {
            return;
        }
        setSelectedColor(color);
        setSelectedImage(undefined);
        scrollCarouselToIndex(0);
        lastScrollIndex.current = NO_SCROLL_INDEX;

        // If color only has one available size, select it, otherwise, clear size selection
        const availableSizesForColor = compatibleSizesByColor?.[color.name];
        if (availableSizesForColor?.length === 1) {
            setSelectedSize(availableSizesForColor[0]);
        } else {
            setSelectedSize(undefined);
        }

        if (availableSizesForColor && availableSizesForColor.length > 0) {
            setSelectedVariantFromDimensions(color.name, availableSizesForColor[0]);
        }
    };

    const handleWishlistButtonClick = () => {
        toggleWishlist({
            variantRecordId: selectedVariant?.primarySizeVariant?.variantRecordId,
            concatenatedId: selectedVariant?.primarySizeVariant?.concatenatedId,
            relewiseVariantId: selectedVariant?.relewiseVariantId,
            relewiseProductId,
            itemId: productItemId,
            size: selectedVariant?.primarySizeVariant?.size,
            variantDisplayName: selectedVariant?.displayName,
            brand,
            color: selectedColor?.name,
            price: selectedVariant?.finalPrice,
            discount: selectedVariant?.discount
        });
    };

    const handleAddToCartWithTracking = () => {
        trackAddToCart();
        handleAddToCartClick();
    };

    return (
        <>
            <BuyboxHeadMetadata
                title={displayName}
                description={productMetaInfo?.description}
                canonicalUrl={context.request.url.requestUrl.origin + context.request.url.requestUrl.pathname}
                imageUrl={activeImage}
                brand={brand}
                allSizes={allSizes}
                productGroupId={productItemId}
                material={productMetaInfo?.composition}
                currencyCode={context.request.channel?.Currency}
                variantsByKey={variantsByKey}
            />
            <HtmlHeadInclude>
                <link rel='preload' as='image' href={preloadImageUrl} />
            </HtmlHeadInclude>
            <section className='new-buybox' data-variant-record-id={selectedVariant?.variantRecordId}>
                <div className='new-buybox__media-grid'>
                    <div className='new-buybox__image-slider' ref={sliderRef}>
                        {selectedVariant &&
                            previewImages?.map((imageUrl, index) => {
                                const title = displayName ? `${displayName} - ${index + 1}` : undefined;
                                return (
                                    <ImageWithLoading
                                        key={`${imageUrl}-${index}`}
                                        wrapperClassname={classnames('new-buybox__slider-image-wrapper', {
                                            'new-buybox__slider-image-wrapper--active': imageUrl === activeImage
                                        })}
                                        src={imageUrl}
                                        alt={title}
                                        title={title}
                                        imageSettings={{ viewports: imageSettings }}
                                        gridSettings={gridSettings ?? {}}
                                        loadFailureBehavior='empty'
                                        showEmptyImageThumbnail
                                        onClick={() => handlePreviewImageClick(imageUrl, index)}
                                        loading={undefined}
                                        fetchPriority={ImageFetchPriority.Low}
                                    />
                                );
                            })}
                    </div>
                    <div className='new-buybox__main-image-container'>
                        <div className='new-buybox__main-carousel' ref={carouselRef} onScroll={handleCarouselScroll}>
                            {previewImages?.map((imageUrl, index) => (
                                <ImageWithLoading
                                    key={`carousel-${imageUrl}-${index}`}
                                    wrapperClassname='new-buybox__main-carousel-item'
                                    src={imageUrl}
                                    alt={displayName}
                                    title={displayName}
                                    imageSettings={{ viewports: imageSettings }}
                                    gridSettings={gridSettings ?? {}}
                                    loadFailureBehavior='empty'
                                    showEmptyImageThumbnail
                                    loading={index === 0 ? 'eager' : undefined}
                                    fetchPriority={index === 0 ? ImageFetchPriority.High : ImageFetchPriority.Low}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <div className='new-buybox__product-info'>
                    <h1 data-testid='buybox-product-title'>{displayName}</h1>
                    {selectedVariant?.badgeLabel && (
                        <OverflowTooltip
                            id='buybox-badge-label'
                            className='new-buybox__label new-buybox__badge-label'
                            isMobile={isMobile}
                            style={{ backgroundColor: selectedVariant.badgeLabel.color, color: selectedVariant.badgeLabel.textColor }}
                        >
                            {selectedVariant.badgeLabel.text.toUpperCase()}
                        </OverflowTooltip>
                    )}
                    <h2 className='new-buybox__price' data-testid='buybox-price-section'>
                        <span
                            className={classnames('new-buybox__final-price', {
                                'new-buybox__final-price--discounted': selectedVariant?.hasDiscount
                            })}
                            data-testid={selectedVariant?.hasDiscount ? 'discounted-price' : 'non-discounted-price'}
                        >
                            {selectedVariant?.hasMemberPrice ? selectedVariant.basePriceFormatted : selectedVariant?.finalPriceFormatted}
                        </span>
                        {selectedVariant?.hasDiscount && (
                            <span className='new-buybox__original-price' data-testid='original-price'>
                                {selectedVariant.basePriceFormatted}
                            </span>
                        )}
                        {!selectedVariant?.hasDiscount && selectedVariant?.inlineLabel && (
                            <OverflowTooltip
                                id='buybox-inline-label'
                                className='new-buybox__label new-buybox__inline-label'
                                isMobile={isMobile}
                                style={{ backgroundColor: selectedVariant.inlineLabel.color, color: selectedVariant.inlineLabel.textColor }}
                            >
                                {selectedVariant.inlineLabel.text.toUpperCase()}
                            </OverflowTooltip>
                        )}
                    </h2>
                    <div id='color-options-label' className='new-buybox__dimension-label'>
                        {resources.productDimensionTypeColor}
                    </div>
                    <ul className='new-buybox__color-options' aria-labelledby='color-options-label' role='radiogroup'>
                        {allColors?.map(color => {
                            const doesColorHaveStock = availabilityByColor ? availabilityByColor[color.name] : true;
                            const tooltipId = sanitizeHtmlId(`buybox-color-${color.name}`);
                            return (
                                <li key={`${color.name}-availability-${doesColorHaveStock}`}>
                                    <InfoTooltip id={tooltipId} isMobile={isMobile} content={color.name}>
                                        <button
                                            type='button'
                                            className={classnames('new-buybox__color-item', {
                                                'new-buybox__color-item--selected': selectedColor?.name === color.name,
                                                'new-buybox__color-item--out-of-stock': !doesColorHaveStock
                                            })}
                                            style={{ '--color-hex': color.hex ?? 'transparent' } as React.CSSProperties}
                                            onClick={() => handleColorSelect(color)}
                                            role='radio'
                                            aria-checked={selectedColor?.name === color.name}
                                            aria-label={color.name}
                                        />
                                    </InfoTooltip>
                                </li>
                            );
                        })}
                    </ul>
                    <div className='new-buybox__selected-color-name' data-testid='buybox-color-name'>{`${
                        selectedColor?.colorRefinerGroup ? `${selectedColor.colorRefinerGroup} / ` : ''
                    }${selectedColor?.name}`}</div>
                    <div id='size-button-label' className='new-buybox__dimension-label'>
                        {resources.productDimensionTypeSize}
                    </div>
                    <button
                        className={classnames('new-buybox__size-trigger', {
                            'new-buybox__size-trigger--open': isSizeModalOpen
                        })}
                        onClick={() => setIsSizeModalOpen(true)}
                        aria-labelledby='size-button-label'
                        aria-haspopup='dialog'
                        aria-expanded={isSizeModalOpen}
                        data-testid='size-select-trigger'
                        disabled={isAvailabilityLoading}
                    >
                        <span data-testid='selected-size-option'>{selectedSize ?? resources.sizeSelectionHeading}</span>
                    </button>
                    <div
                        className={classnames('new-buybox__out-of-stock-message', {
                            'new-buybox__out-of-stock-message--visible': currentSelectionOutOfStock
                        })}
                        role='alert'
                    >
                        {resources.outOfStockText}
                    </div>

                    {isSizeModalOpen && (
                        <div className='new-buybox__size-modal-overlay' onClick={() => setIsSizeModalOpen(false)}>
                            <div
                                className='new-buybox__size-modal'
                                role='dialog'
                                aria-modal='true'
                                aria-labelledby='size-modal-title'
                                tabIndex={-1}
                                ref={node => node?.focus()}
                                onClick={e => e.stopPropagation()}
                                onKeyDown={handleSizeModalKeyDown}
                            >
                                <div className='new-buybox__size-modal-header'>
                                    <span id='size-modal-title' className='new-buybox__size-modal-title'>
                                        {resources.productDimensionTypeSize}
                                    </span>
                                    <button
                                        className='new-buybox__size-modal-close'
                                        onClick={() => setIsSizeModalOpen(false)}
                                        data-testid='size-modal-close-button'
                                        aria-label={resources.closeButtonLabel}
                                        type='button'
                                    >
                                        &times;
                                    </button>
                                </div>
                                <ul
                                    className='new-buybox__size-options'
                                    data-testid='size-select-options'
                                    role='radiogroup'
                                    aria-labelledby='size-modal-title'
                                >
                                    {availableSizes?.map(size => {
                                        const doesSizeHaveStock = availabilityForSelectedColorBySize
                                            ? availabilityForSelectedColorBySize[size]
                                            : true;
                                        return (
                                            <li key={size}>
                                                <button
                                                    type='button'
                                                    className={classnames('new-buybox__size-item', {
                                                        'new-buybox__size-item--selected': selectedSize === size,
                                                        'new-buybox__size-item--out-of-stock': !doesSizeHaveStock
                                                    })}
                                                    onClick={() => handleSizeSelect(size)}
                                                    role='radio'
                                                    aria-checked={selectedSize === size}
                                                    data-testid='size-option'
                                                >
                                                    {size}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    )}

                    <Button
                        className='new-buybox__add-to-cart-button'
                        disabled={!selectedSize || !selectedColor || currentSelectionOutOfStock || isAvailabilityLoading}
                        onClick={handleAddToCartWithTracking}
                        isLoading={addToCartLoading}
                        data-testid='add-to-cart-button'
                    >
                        {resources.addToCartText}
                    </Button>
                    {hasAddToCartError && (
                        <div className='new-buybox__add-to-cart-error-message'>{resources.addedToCartFailureMessage}</div>
                    )}
                    <Button
                        className={classnames('new-buybox__add-to-wishlist-button', {
                            'new-buybox__add-to-wishlist-button--in-wishlist': isVariantInWishlist
                        })}
                        variant='secondary'
                        iconName={isVariantInWishlist ? 'heart-filled' : 'heart'}
                        onClick={handleWishlistButtonClick}
                        isLoading={isWishlistLoading}
                        data-testid='add-to-wishlist-button'
                        data-wishlist-state={isVariantInWishlist ? 'active' : 'inactive'}
                        disabled={!isWishlistReady}
                    >
                        {isVariantInWishlist ? resources.removeFromWishlistButtonText : resources.addToWishlistButtonText}
                    </Button>
                    {addToWishlistError && (
                        <div className='new-buybox__add-to-wishlist-error-message'>{resources.addItemToWishlistError}</div>
                    )}

                    <CollapsibleGroup className='new-buybox__product-meta-info-section' items={metaInfoData} />
                </div>
            </section>
        </>
    );
};

export default NewBuybox;
